import {
  faCircleDollarToSlot,
  faPen,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssistChip, Button, CopyableText, Stack, Text } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs';
import { Heading } from '~/components/molecules/Heading';
import { getIapPollInfo } from '~/components/molecules/IapPollListItem/getIapPollInfo';
import { AppBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { IapPoll } from '~/components/organisms/IapPoll';
import { useIapPollQuery } from '~/lib/api/modules/IapPolls/hooks/useIapPollQuery';

export type IAppIapPollPageProps = {
  appId: string;
  iapPollId: string;
};

export const AppIapPollPage: React.FC<IAppIapPollPageProps> = (props) => {
  const { appId, iapPollId } = props;
  const { t } = useTranslation();
  const iapPollQuery = useIapPollQuery({
    appId,
    iapPollId,
  });

  const iapPoll = iapPollQuery.data;
  const parentUrl = `/apps/${appId}/iapPolls`;
  const iapPollInfo = iapPoll ? getIapPollInfo(iapPoll) : undefined;

  return (
    <>
      <Helmet>
        <title>{t('AppIapPollPage.seo.title')}</title>
      </Helmet>

      <AppBreadcrumbs appId={appId}>
        <Breadcrumbs.Action href={parentUrl}>
          <FontAwesomeIcon icon={faCircleDollarToSlot} />
          {t('AppIapPollsPage.title')}
        </Breadcrumbs.Action>
      </AppBreadcrumbs>

      <Heading
        title={
          <QuerySkeleton query={iapPollQuery} length={{ min: 8, max: 16 }}>
            <Stack horizontal gap={3}>
              {t('AppIapPollPage.title', { name: iapPollQuery.data?.title })}
              {iapPollInfo && (
                <AssistChip label={t(`IapPollInfo.${iapPollInfo.status}`)} />
              )}
            </Stack>
          </QuerySkeleton>
        }
        subtitle={
          <Text variant="body" size="md">
            {t('AppIapPollPage.subtitle')}{' '}
            <CopyableText>{iapPollId}</CopyableText>
          </Text>
        }
        previousUrl={parentUrl}
        actions={
          <Stack horizontal gap={3}>
            <Button
              href={`/apps/${appId}/iapPolls/${iapPollId}/update`}
              icon={<FontAwesomeIcon icon={faPen} />}
            >
              {t('common.edit')}
            </Button>
          </Stack>
        }
      />

      <IapPoll appId={appId} iapPollId={iapPollId} />
    </>
  );
};
